import { DragState, GameState, Layout, Particle } from './types';
import { createInitialState, finishClear, placePiece, updateParticles } from './game';
import { computeLayout, drawMenu, getGhostCell, inRect, menuRects, readTheme, renderFrame } from './renderer';
import { attachDragListeners } from './input';
import { track } from './analytics';
import { t } from './i18n';
import { SCENES } from './scenes';
import { sceneId, track as ctrack, canInstall, promptInstall } from './campaign';

// ── Audio ─────────────────────────────────────────────────────────────────────
let audioCtx: AudioContext | null = null;
try {
  audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
} catch { /* audio unavailable on this device */ }

const resumeCtx = () => { audioCtx?.resume(); };
document.addEventListener('touchstart', resumeCtx, { once: true });
document.addEventListener('click',      resumeCtx, { once: true });

function playTone(freq: number, duration: number, volume = 0.15, type: OscillatorType = 'sine'): void {
  if (!audioCtx || audioCtx.state === 'suspended') return;
  try {
    const osc  = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(volume, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + duration);
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start();
    osc.stop(audioCtx.currentTime + duration);
  } catch { /* ignore */ }
}

// Soft "тук" — piece landed on the grid.
function playPlace(volume = 0.5): void {
  playTone(300, 0.08, volume, 'sine');
}

// Rising sweep — one or more lines cleared.
function playClear(volume = 0.7): void {
  if (!audioCtx || audioCtx.state === 'suspended') return;
  try {
    const osc  = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(400, audioCtx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(900, audioCtx.currentTime + 0.22);
    gain.gain.setValueAtTime(volume, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + 0.25);
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start();
    osc.stop(audioCtx.currentTime + 0.25);
  } catch { /* ignore */ }
}

// Bright three-note up-arpeggio — combo (2+ simultaneous line clears).
function playCombo(volume = 0.8): void {
  playTone(660, 0.10, volume, 'triangle');
  setTimeout(() => playTone(880,  0.10, volume, 'triangle'), 90);
  setTimeout(() => playTone(1100, 0.15, volume, 'triangle'), 180);
}

// ── Canvas setup ──────────────────────────────────────────────────────────────
const canvas = document.getElementById('game') as HTMLCanvasElement;
const ctx    = canvas.getContext('2d')!;
let W = 0, H = 0;

function resize(): void {
  const w = window.innerWidth;
  const h = window.innerHeight;
  if (!w || !h) return;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  W = w; H = h;
  canvas.width  = W * dpr;
  canvas.height = H * dpr;
  canvas.style.width  = `${W}px`;
  canvas.style.height = `${H}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}
resize();
new ResizeObserver(resize).observe(document.body);
document.addEventListener('visibilitychange', () => { if (!document.hidden) resize(); });
window.addEventListener('pageshow', (e) => { if (e.persisted) resize(); });

// ── Best score persistence ───────────────────────────────────────────────────
const BEST_KEY = 'game-block-blast-best';
function loadBest(): number {
  try { return Number(localStorage.getItem(BEST_KEY)) || 0; } catch { return 0; }
}
function saveBest(best: number): void {
  try { localStorage.setItem(BEST_KEY, String(best)); } catch { /* ignore */ }
}

// ── Campaign landing (?scene) ───────────────────────────────────────────────
// Засеянная доска из рекламы. При активной сцене рекорд не пишем (эфемерно).
const scene = sceneId ? SCENES[sceneId] ?? null : null;
const campaign = !!scene;
let campaignFlow = campaign;
let firstMoveTracked = false;
let clearTracked = false;
let ctaShown = false;
let pendingCta = false;   // ставится, когда кампанийный дроп запустил клир

function persistBest(best: number): void {
  if (!campaign) saveBest(best);
}

// ── State ─────────────────────────────────────────────────────────────────────
let state: GameState = campaign
  ? (() => { const b = scene!.build(); return { ...createInitialState(loadBest()), grid: b.grid, tray: b.tray }; })()
  : createInitialState(loadBest());
const theme = readTheme();
let layout: Layout  = computeLayout(W, H);
let drag: DragState | null = null;
let pendingStart = !campaign;   // кампанийная посадка сразу интерактивна (крючок сверху)
let menuOpen = false;

// ── Analytics (события раунда) ────────────────────────────────────────────────
let ended = false;         // game_over — один раз на раунд
let recordBeaten = false;  // new_best — один раз, при первом превышении рекорда
let recordBaseline = state.best;
function reportStart(): void {
  ended = false;
  recordBeaten = false;
  recordBaseline = state.best;
  track('game_start');
}
reportStart(); // первый раунд (boot)

function restartGame(): void {
  dragHandle.reset();
  drag = null;
  const best = state.best;
  state = { ...createInitialState(), best };
  reportStart();
}

// ── Particle helpers ──────────────────────────────────────────────────────────
function spawnParticles(state: GameState): Particle[] {
  if (!state.clearAnim) return state.particles;
  const { rows, cols } = state.clearAnim;
  const particles: Particle[] = [...state.particles];
  const colorSet = new Set<string>();

  for (const r of rows) {
    for (let c = 0; c < 8; c++) {
      const color = state.grid[r][c];
      if (color) colorSet.add(theme.colors[color]);
    }
  }
  for (const c of cols) {
    for (let r = 0; r < 8; r++) {
      const color = state.grid[r][c];
      if (color) colorSet.add(theme.colors[color]);
    }
  }

  const colors = [...colorSet];
  const count  = (rows.length + cols.length) * 4;
  for (let i = 0; i < count; i++) {
    const r   = rows.length > 0 ? rows[Math.floor(Math.random() * rows.length)] : 0;
    const c   = cols.length > 0 ? cols[Math.floor(Math.random() * cols.length)] : 0;
    const x   = layout.gridX + (c + Math.random()) * layout.cell;
    const y   = layout.gridY + (r + Math.random()) * layout.cell;
    particles.push({
      x, y,
      vx:    (Math.random() - 0.5) * 300,
      vy:    -Math.random() * 350 - 80,
      color: colors[Math.floor(Math.random() * colors.length)] ?? '#ffffff',
      life:  1,
      size:  Math.random() * 4 + 2,
    });
  }
  return particles;
}

// ── Menu (☰ → overlay: Resume / Restart / Home) ─────────────────────────────────
// Registered before the drag listeners / tap-to-start listener below so the menu
// intercepts a tap (via stopImmediatePropagation) before any game logic runs.
canvas.addEventListener('pointerdown', (e) => {
  if (pendingStart) return;
  const rect = canvas.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;
  const m = menuRects(W, H);

  if (menuOpen) {
    e.preventDefault();
    e.stopImmediatePropagation();
    if (inRect(x, y, m.resume))       { menuOpen = false; }
    else if (inRect(x, y, m.restart)) { menuOpen = false; restartGame(); }
    else if (inRect(x, y, m.hub))     { location.href = document.body.dataset.hubUrl ?? '/'; }
    else                               { menuOpen = false; } // tap outside items closes menu
    return;
  }
  if (state.phase !== 'game_over' &&
      Math.hypot(x - m.toggle.cx, y - m.toggle.cy) <= m.toggle.r + 6) {
    e.preventDefault();
    e.stopImmediatePropagation();
    menuOpen = true;
  }
}, { passive: false });

// ── Input handling ────────────────────────────────────────────────────────────
const dragHandle = attachDragListeners(
  canvas,
  () => layout,
  () => state.tray,
  () => state.phase === 'placing',
  (d) => { drag = d; },
  (d) => { drag = d; },
  (d) => {
    // Drop
    const piece = state.tray[d.trayIdx];
    if (piece && state.phase === 'placing') {
      const ghost = getGhostCell(d, piece, layout);
      if (ghost) {
        const next = placePiece(state, d.trayIdx, ghost.row, ghost.col, performance.now());
        if (next !== state) {
          state = next;
          persistBest(state.best);
          if (campaignFlow && !firstMoveTracked) {
            firstMoveTracked = true;
            ctrack('campaign_first_move');
          }
          if (state.phase === 'clearing') {
            playClear(0.7);
            if (campaignFlow) pendingCta = true;   // жирный клир → покажем CTA после анимации
          } else {
            playPlace(0.5);
          }
        }
      }
    }
    drag = null;
  },
  () => { drag = null; },
);

// Tap to start / restart
canvas.addEventListener('pointerdown', () => {
  if (pendingStart) {
    audioCtx?.resume();
    pendingStart = false;
    return;
  }
  if (state.phase === 'game_over') {
    restartGame();
  }
}, { passive: true });

// ── Campaign overlays (hook + install CTA) ─────────────────────────────────────
const hookOverlay = document.getElementById('hook-overlay') as HTMLDivElement;
const hookTitle   = document.getElementById('hook-title')   as HTMLDivElement;
const hookPlay    = document.getElementById('hook-play')    as HTMLButtonElement;
const ctaOverlay  = document.getElementById('cta-overlay')  as HTMLDivElement;
const ctaTitle    = document.getElementById('cta-title')    as HTMLDivElement;
const ctaInstall  = document.getElementById('cta-install')  as HTMLButtonElement;
const ctaAgain    = document.getElementById('cta-again')    as HTMLButtonElement;

if (campaign && scene) {
  hookTitle.textContent  = t(scene.headlineKey);
  hookPlay.textContent   = t('hook_play');
  ctaTitle.textContent   = t('cta_win_title');
  ctaInstall.textContent = t('cta_install');
  ctaAgain.textContent   = t('cta_again');
  hookOverlay.classList.add('open');
  ctrack('campaign_land');
}

hookPlay.addEventListener('click', (e) => {
  e.stopPropagation();
  hookOverlay.classList.remove('open');
});

ctaInstall.addEventListener('click', (e) => {
  e.stopPropagation();
  promptInstall();
});

ctaAgain.addEventListener('click', (e) => {
  e.stopPropagation();
  ctrack('campaign_continue');
  campaignFlow = false;
  ctaOverlay.classList.remove('open');
  restartGame();
});

// ── Game loop ─────────────────────────────────────────────────────────────────
let lastMs = 0;

function loop(nowMs: number): void {
  try {
    const dtMs = lastMs > 0 ? Math.min(nowMs - lastMs, 100) : 16;
    lastMs = nowMs;

    layout = computeLayout(W, H);

    // A stale open menu must not swallow the next tap on the game-over screen.
    if (state.phase === 'game_over') {
      menuOpen = false;
      if (!ended) { ended = true; track('game_over', { result: 'lose', score: state.score }); }
    }
    if (!campaign && !recordBeaten && state.best > recordBaseline) {
      recordBeaten = true;
      track('new_best', { score: state.best });
    }

    // Update particles
    if (state.particles.length > 0) {
      state = { ...state, particles: updateParticles(state.particles, dtMs) };
    }

    // Finish clear animation
    if (state.phase === 'clearing' && state.clearAnim) {
      const elapsed = nowMs - state.clearAnim.startMs;
      if (elapsed >= state.clearAnim.durationMs) {
        const newParticles = spawnParticles(state);
        state = finishClear(state, newParticles);
        persistBest(state.best);
        if (state.combo >= 2) playCombo(0.8);
        else playPlace(0.4);

        // Кампанийная воронка: жирный клир состоялся → показать install-CTA.
        if (pendingCta && campaignFlow) {
          pendingCta = false;
          if (!clearTracked) {
            clearTracked = true;
            ctrack('campaign_big_clear', { score: state.score });
          }
          ctaInstall.style.display = canInstall() ? '' : 'none';
          ctaOverlay.classList.add('open');
          if (!ctaShown) {
            ctaShown = true;
            ctrack('campaign_install_prompt', { install_available: canInstall() });
          }
        }
      }
    }

    renderFrame(ctx, W, H, layout, state, theme, nowMs, drag, pendingStart);
    drawMenu(ctx, W, H, theme, state.phase, pendingStart, menuOpen);
  } catch (e) {
    console.error('[block-blast] loop error', e);
  }
  requestAnimationFrame(loop);
}

requestAnimationFrame(loop);
